import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Heart,
  Target,
  Users2,
  Star,
  MapPin,
  Calendar,
  Globe2,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import blob from "@/assets/blob-accent.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Us - WeLivdIt" },
      {
        name: "description",
        content:
          "We're Specialists who've lived the visa interview. Now we help others walk in prepared and walk out approved.",
      },
      { property: "og:title", content: "About Us - WeLivdIt" },
      {
        property: "og:description",
        content:
          "Specialists who've lived the visa interview, helping students walk in prepared and walk out approved.",
      },
      { property: "og:url", content: "https://welivdit.com/about" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "WeLivdIt Private Limited",
          url: "https://welivdit.com",
          description: "The WeLivdIt Method helps students clear US visa interviews with 1-on-1 mock sessions, DS-160 review, and 214(b) refusal recovery. Built on 1,500+ approvals.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Hyderabad",
            addressRegion: "Telangana",
            addressCountry: "IN",
          },
          identifier: "CIN U74999TG2021PTC149756",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.7",
            reviewCount: "264",
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <main className="min-h-screen bg-background">
      {/* HERO SECTION */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <img
          src={blob}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -top-40 -left-32 w-[600px] opacity-60 blur-2xl"
        />
        <div className="mx-auto max-w-4xl px-5 pt-16 pb-20 md:pt-28 md:pb-24 relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-primary/20 mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Built By Alumni
          </div>
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-foreground text-balance leading-[1.05]">
            We&apos;ve been on <em className="not-italic text-primary">your side</em> of the window.
          </h1>
          <p className="mt-6 text-base md:text-xl text-muted-foreground leading-relaxed">
            WeLivdIt started because we remember what the night before our visa interview felt like — the doubt, the
            over-googling, the half-good advice from strangers on Reddit. We built the prep we wish we&apos;d had.
          </p>
        </div>
      </section>

      {/* WHO WE ARE & STORY */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Main Story Paragraphs */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">Who We Are</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground text-balance">
                A mentorship platform built by people who walked in first.
              </h2>
            </div>

            <div className="space-y-4 text-foreground/80 leading-relaxed text-sm md:text-base">
              <p>
                <span className="font-semibold text-foreground">WeLivdIt</span> is a one-on-one mentorship platform for
                students aspiring to study abroad and professionals planning to upskill or change paths. We connect you
                with a senior who has already walked the road you&apos;re about to take — someone who answered the same
                DS-160 questions, sat in front of the same consular officer, and figured out what actually moves the
                needle.
              </p>
              <p>
                Founded in <span className="font-semibold text-foreground">2021 in Hyderabad, India</span>, we started
                with a simple belief: the night before a US visa interview shouldn&apos;t feel like guesswork. Generic
                checklists and Reddit threads weren&apos;t cutting it. So we built the prep we wish we&apos;d had —
                structured mocks, brutal-but-kind feedback, and a Specialist in your corner from DS-160 to the consulate
                gate.
              </p>
              <p>
                Today, our team of mentors has helped{" "}
                <span className="font-semibold text-foreground">1,500+ students since 2021</span> across F-1, F-2, J-1, and H-1B
                interviews — earning a <span className="font-semibold text-foreground">4.7★ rating on Trustpilot</span>.
                We don&apos;t promise outcomes. We promise preparation so thorough that you walk in with nothing left to
                second-guess.
              </p>
              <p>
                What sets WeLivdIt apart is the depth of our one-on-one engagement. Every mentor on our roster has
                personally cleared a US visa interview, lived through the DS-160 paperwork, navigated SEVIS fee
                receipts, and learned what consular officers in India actually listen for. That lived experience shapes
                every mock interview, every piece of feedback, and every revision we suggest to your story.
              </p>
              <p>
                We are headquartered in Hyderabad, Telangana, and operate as{" "}
                <span className="font-semibold text-foreground">WeLivdIt Private Limited</span> (CIN
                U74999TG2021PTC149756), a registered Indian company. Coaching is delivered entirely online over secure
                video sessions, scheduled in IST around your interview date, and backed by transparent pricing in Indian
                Rupees, a clear refund policy, and a published Grievance Officer.
              </p>
              <p>
                Beyond paid coaching, we run a free WhatsApp success community of 1,500+ approved students who share
                recent interview experiences, slot-booking strategies, document checklists, and small psychological wins
                that get you through the toughest week of your study-abroad journey.
              </p>
            </div>

            {/* Grid of Key Stats */}
            <div className="pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Calendar, label: "Founded", value: "2021" },
                { icon: MapPin, label: "Based in", value: "Hyderabad" },
                { icon: Users2, label: "Students", value: "1,500+" },
                { icon: Star, label: "Trustpilot", value: "4.7 ★" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-border bg-card p-4 transition hover:border-primary/40 hover:-translate-y-0.5 shadow-sm"
                >
                  <s.icon className="h-4 w-4 text-primary" />
                  <div className="mt-2 font-display text-xl md:text-2xl font-bold tracking-tight text-foreground">
                    {s.value}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wider mt-0.5">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDEBAR: "What We Actually Do" */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 rounded-3xl bg-card border border-border p-7 md:p-8 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 h-32 w-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
                <Globe2 className="h-5 w-5" />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">What We Actually Do</h2>
            </div>

            <ul className="mt-6 space-y-4 text-sm text-foreground/80">
              {[
                {
                  title: "1-on-1 mock interviews",
                  desc: "Drilled with a Specialist who has walked into the consulate window.",
                },
                {
                  title: "DS-160 form review",
                  desc: "Every single line, field, and financial red flag audited.",
                },
                {
                  title: "Visa officer logic",
                  desc: "Learn to answer curveball follow-ups with calm structure.",
                },
                {
                  title: "Refusal recovery",
                  desc: "Root-cause analysis and re-attempt strategy after 214(b).",
                },
                {
                  title: "Community access",
                  desc: "Free WhatsApp circle with 1,500+ approved students.",
                },
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 bg-muted/30 border border-border/60 rounded-xl p-3.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">{item.title}</span>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-border/60">
              <Link
                to="/services"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-6 py-3 text-sm font-semibold hover:opacity-95 shadow-glow transition"
              >
                Explore Services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="border-y border-border/60 bg-muted/20 py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-wider text-primary">What We Believe</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Three things we won&apos;t compromise on.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: "Founded by alumni",
                body: "Every Specialist has been through the interview themselves. No theoretical advice — only what actually works in the room.",
              },
              {
                icon: Target,
                title: "Outcome-obsessed",
                body: "We measure ourselves strictly on visa approvals, not hours sold. That changes how deeply we invest in your preparation.",
              },
              {
                icon: ShieldCheck,
                title: "4.7★ Proven Methodology",
                body: "Tested across 1,500+ student cases — our structured 6-layer prep system consistently turns tough interviews into approvals.",
              },
            ].map((v) => (
              <div
                key={v.title}
                className="rounded-3xl bg-card border border-border p-7 transition hover:-translate-y-1 hover:border-primary/40 shadow-sm"
              >
                <div className="h-12 w-12 rounded-2xl bg-accent grid place-items-center text-primary">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-foreground">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD & SPECIALISTS BANNER */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-12 shadow-sm relative overflow-hidden">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-[11px] font-bold uppercase tracking-wider text-primary">
            The WeLivdIt Method
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Meet the WeLivdIt Specialists
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl text-sm md:text-base">
            Our Specialists bring 5+ years of dedicated experience in US visa interview preparation, having guided
            1,500+ students to approval since 2021 across F-1, B1/B2, and refusal-recovery cases. Each case is reviewed by a
            Specialist who has seen the pattern before: the DS-160 mismatch, the financial gap, the story that
            doesn&apos;t add up under follow-up questioning.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: "5+", l: "Years Experience" },
              { n: "1,500+", l: "Approvals" },
              { n: "5", l: "Consulates Covered" },
              { n: "100%", l: "Confidential Review" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-border/80 bg-muted/40 p-5 text-center">
                <div className="font-display text-2xl md:text-3xl font-bold text-primary">{s.n}</div>
                <div className="mt-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE CTA SECTION */}
      <section className="bg-card border-t border-border">
        <div className="mx-auto max-w-4xl px-5 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-foreground">Our Promise</h2>
          <p className="mt-6 text-base md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            We don&apos;t sell scripts. We don&apos;t sell luck. We sell{" "}
            <span className="text-primary font-semibold">
              preparation that travels with you into the interview room
            </span>
            . The clarity to answer well, the calm to look up and smile, and the structure to make every word count.
          </p>
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-95 transition"
            >
              Enroll in our plans <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
